import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteContent } from '../content/SiteContentProvider';
import { getSeoConfig } from '../lib/seo';
import { toSeoImageUrl } from '../lib/seoImages';

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function setLinkRel(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function setStructuredData(data?: Record<string, unknown> | Record<string, unknown>[]) {
  const scriptId = 'gameside-structured-data';
  const existing = document.getElementById(scriptId);

  if (!data) {
    existing?.remove();
    return;
  }

  const script = existing || document.createElement('script');
  script.id = scriptId;
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);

  if (!existing) {
    document.head.appendChild(script);
  }
}

export default function SeoManager() {
  const location = useLocation();
  const { content } = useSiteContent();

  useEffect(() => {
    const origin = window.location.origin;
    const config = getSeoConfig(location.pathname, content, origin);
    const seoImage = toSeoImageUrl(config.image, config.imageAlt, config.title) || config.image;
    const imageUrl = seoImage.startsWith('http') ? seoImage : `${origin}${seoImage}`;

    document.documentElement.lang = 'fr';
    document.title = config.title;

    setMeta('meta[name="description"]', 'name', 'description', config.description);
    setMeta('meta[name="keywords"]', 'name', 'keywords', config.keywords);
    setMeta('meta[name="robots"]', 'name', 'robots', config.robots);
    setMeta('meta[name="author"]', 'name', 'author', content.siteName);
    setMeta('meta[property="og:title"]', 'property', 'og:title', config.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', config.description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', config.ogType);
    setMeta('meta[property="og:url"]', 'property', 'og:url', config.canonicalUrl);
    setMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    setMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', config.imageAlt);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', content.siteName);
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', 'fr_FR');
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:site"]', 'name', 'twitter:site', '@gamesidefrance');
    setMeta('meta[name="twitter:creator"]', 'name', 'twitter:creator', '@gamesidefrance');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', config.title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', config.description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    setMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', config.imageAlt);
    setLinkRel('canonical', config.canonicalUrl);
    setStructuredData(config.structuredData);
  }, [content, location.pathname]);

  return null;
}
